import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { 
  Tabs, 
  Tab, 
  Box, 
  Chip, 
  useTheme, 
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  MoreVert as MoreVertIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon 
} from '@mui/icons-material';
import { useResponsive } from '../../hooks/useResponsive';

/**
 * WalteroTabs - Brand-compliant tab navigation component
 * 
 * Features:
 * - Waltero brand styling with proper color palette
 * - Responsive design with mobile overflow handling
 * - Accessibility compliant with WCAG standards
 * - Support for badge/chip indicators
 * - Icon support for tabs
 * - Scrollable tabs on overflow
 * - Mobile-friendly touch targets
 * 
 * @param {Object} props Component properties
 * @param {Array} props.tabs - Array of tab objects {id, label, icon?, badge?, disabled?, adminOnly?}
 * @param {string} props.value - Currently active tab id
 * @param {function} props.onChange - Callback when tab changes (tabId) => void
 * @param {string} props.variant - Tab variant: 'standard', 'scrollable', 'fullWidth'
 * @param {string} props.orientation - Tab orientation: 'horizontal', 'vertical'
 * @param {boolean} props.indicatorColor - Use brand indicator color (default: true)
 * @param {boolean} props.centered - Center tabs (when not scrollable)
 * @param {Object} props.sx - Additional styling
 * @param {string} props.ariaLabel - Accessibility label for tab group
 */
const WalteroTabs = ({
  tabs = [],
  value,
  onChange,
  variant = 'scrollable',
  orientation = 'horizontal',
  indicatorColor = true,
  centered = false,
  sx = {},
  ariaLabel = 'Navigation tabs',
  showAdminBadges = true,
  ...props
}) => {
  const theme = useTheme();
  const { breakpoints, device, touch } = useResponsive();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  
  // Responsive configuration
  const isMobileView = breakpoints.isMobile;
  const isSmallMobile = breakpoints.isSmallMobile;
  
  // Dynamic variant based on screen size and tab count
  const getResponsiveVariant = () => {
    if (variant !== 'scrollable') return variant;
    
    if (isMobileView && tabs.length > 3) return 'scrollable';
    if (isMobileView && tabs.length <= 3) return 'fullWidth';
    if (tabs.length > 5) return 'scrollable';
    return 'standard';
  };
  
  const responsiveVariant = getResponsiveVariant();
  
  // Handle tab change
  const handleTabChange = useCallback((event, newValue) => {
    onChange?.(newValue);
  }, [onChange]);
  
  // Mobile dropdown menu handlers
  const handleMobileMenuOpen = useCallback((event) => {
    setMobileMenuAnchor(event.currentTarget);
  }, []);
  
  const handleMobileMenuClose = useCallback(() => {
    setMobileMenuAnchor(null);
  }, []);
  
  const handleMobileMenuSelect = useCallback((tabId) => {
    onChange?.(tabId);
    handleMobileMenuClose();
  }, [onChange, handleMobileMenuClose]);
  
  // Filter accessible tabs
  const accessibleTabs = tabs.filter(tab => !tab.disabled);
  
  // Get current tab for mobile display
  const currentTab = tabs.find(tab => tab.id === value);
  
  // Custom styling for Waltero brand compliance
  const getTabStyling = () => ({
    minHeight: device.isTouchDevice ? touch.minTouchTarget : 48,
    textTransform: 'none', // Override Material-UI default
    fontFamily: theme.typography.button.fontFamily,
    fontWeight: 500,
    fontSize: breakpoints.isMobile ? '0.875rem' : '1rem',
    letterSpacing: '0.01em',
    padding: breakpoints.isMobile 
      ? theme.spacing(1.5, 2) 
      : theme.spacing(1.5, 3),
    color: theme.palette.waltero.primary.mediumBlue,
    '&.Mui-selected': {
      color: theme.palette.waltero.secondary.primaryApricot,
      fontWeight: 600
    },
    '&:hover': {
      color: theme.palette.waltero.secondary.primaryApricot,
      backgroundColor: `${theme.palette.waltero.secondary.backgroundApricot}40`
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.waltero.secondary.primaryApricot}`,
      outlineOffset: '2px'
    }
  });
  
  // Tab indicator styling
  const getIndicatorStyling = () => ({
    backgroundColor: indicatorColor 
      ? theme.palette.waltero.secondary.primaryApricot 
      : theme.palette.primary.main,
    height: 3,
    borderRadius: '2px 2px 0 0'
  });
  
  // Render individual tab
  const renderTab = (tab) => {
    const { id, label, icon: Icon, badge, adminOnly, disabled } = tab;
    
    return (
      <Tab
        key={id}
        value={id}
        disabled={disabled}
        icon={Icon ? <Icon /> : undefined}
        iconPosition="start"
        sx={getTabStyling()}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{label}</span>
            {badge && (
              <Chip 
                label={badge}
                size="small"
                sx={{ 
                  height: 20,
                  fontSize: '0.6rem',
                  bgcolor: theme.palette.waltero.secondary.lightApricot,
                  color: theme.palette.white
                }}
              />
            )}
            {adminOnly && showAdminBadges && (
              <Chip 
                label="Admin"
                size="small"
                sx={{ 
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: theme.palette.waltero.secondary.darkApricot,
                  color: theme.palette.white
                }}
              />
            )}
          </Box>
        }
      />
    );
  };
  
  // Mobile dropdown fallback for many tabs
  if (isSmallMobile && tabs.length > 4) {
    return (
      <Box sx={{ mb: 3, ...sx }}>
        {/* Mobile dropdown trigger */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          bgcolor: theme.palette.background.paper,
          borderRadius: 1,
          border: `1px solid ${theme.palette.waltero.primary.neutralBlue}`,
          p: 1
        }}>
          <Box sx={{ flex: 1, px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {currentTab?.icon && <currentTab.icon />}
              <span style={{ fontWeight: 500 }}>
                {currentTab?.label || 'Select Tab'}
              </span>
              {currentTab?.adminOnly && showAdminBadges && (
                <Chip 
                  label="Admin"
                  size="small"
                  sx={{ 
                    height: 18,
                    fontSize: '0.6rem',
                    bgcolor: theme.palette.waltero.secondary.darkApricot,
                    color: theme.palette.white
                  }}
                />
              )}
            </Box>
          </Box>
          <IconButton 
            onClick={handleMobileMenuOpen}
            size="small"
            aria-label="Open tab menu"
          >
            <KeyboardArrowDownIcon />
          </IconButton>
        </Box>
        
        {/* Mobile dropdown menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          PaperProps={{
            sx: {
              maxHeight: '70vh',
              width: '90vw',
              maxWidth: 320
            }
          }}
        >
          {accessibleTabs.map(tab => (
            <MenuItem
              key={tab.id}
              selected={tab.id === value}
              onClick={() => handleMobileMenuSelect(tab.id)}
              sx={{
                minHeight: touch.minTouchTarget,
                '&.Mui-selected': {
                  backgroundColor: `${theme.palette.waltero.secondary.backgroundApricot}80`
                }
              }}
            >
              {tab.icon && (
                <ListItemIcon>
                  <tab.icon />
                </ListItemIcon>
              )}
              <ListItemText 
                primary={tab.label}
                secondary={tab.adminOnly && showAdminBadges ? 'Admin only' : undefined}
              />
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  }
  
  // Standard tab rendering
  return (
    <Box sx={{ mb: 3, ...sx }}>
      <Tabs
        value={value}
        onChange={handleTabChange}
        variant={responsiveVariant}
        orientation={orientation}
        centered={centered && responsiveVariant !== 'scrollable'}
        scrollButtons={responsiveVariant === 'scrollable' ? 'auto' : false}
        allowScrollButtonsMobile={true}
        aria-label={ariaLabel}
        sx={{
          '& .MuiTabs-flexContainer': {
            gap: breakpoints.isMobile ? 0 : theme.spacing(0.5)
          },
          '& .MuiTabs-indicator': getIndicatorStyling(),
          '& .MuiTabs-scrollButtons': {
            color: theme.palette.waltero.primary.mediumBlue,
            '&:hover': {
              backgroundColor: `${theme.palette.waltero.secondary.backgroundApricot}40`
            }
          },
          // Ensure tab panel accessibility
          '& [role="tabpanel"]': {
            mt: 2
          }
        }}
        {...props}
      >
        {accessibleTabs.map(renderTab)}
      </Tabs>
    </Box>
  );
};

WalteroTabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
    adminOnly: PropTypes.bool
  })).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['standard', 'scrollable', 'fullWidth']),
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  indicatorColor: PropTypes.bool,
  centered: PropTypes.bool,
  sx: PropTypes.object,
  ariaLabel: PropTypes.string,
  showAdminBadges: PropTypes.bool
};

/**
 * WalteroTabPanel - Companion component for tab content
 * 
 * @param {Object} props Component properties
 * @param {React.Node} props.children Content to display
 * @param {string} props.value Current tab value
 * @param {string} props.index This panel's tab index
 * @param {Object} props.sx Additional styling
 */
export const WalteroTabPanel = ({ children, value, index, sx = {}, ...props }) => {
  const theme = useTheme();
  
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`waltero-tabpanel-${index}`}
      aria-labelledby={`waltero-tab-${index}`}
      style={{
        display: value === index ? 'block' : 'none',
      }}
      {...props}
    >
      {value === index && (
        <Box sx={{ 
          pt: theme.spacing(2), 
          ...sx 
        }}>
          {children}
        </Box>
      )}
    </div>
  );
};

WalteroTabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.string.isRequired,
  index: PropTypes.string.isRequired,
  sx: PropTypes.object
};

export default WalteroTabs;